import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AutosaveControlService, AutosavePage } from '@service/auto-save-controller.service';
import { GaugeExtensionService } from '@service/canvas/gauge-extension.service';
import { MAX_SCALE_DIVISION_VALUE } from 'icreate/common/models/graphs';

export interface GaugeDivisionsDialogData {
    min: number;
    max: number;
    decimalPlaces: number;
    divisions: number;
    scaleValues?: number[];
    autoSavePage?: AutosavePage;
}

export interface GaugeDivisionsDialogResult {
    divisions: number;
    scaleValues: number[];
}

@Component({
    selector: 'app-gauge-divisions-dialog',
    templateUrl: './gauge-divisions-dialog.component.html',
    styleUrl: './gauge-divisions-dialog.component.css',
    standalone: false,
})
export class GaugeDivisionsDialogComponent implements OnInit, OnDestroy {
    readonly DivisionOptions = Array.from({ length: MAX_SCALE_DIVISION_VALUE }, (_, i) => i + 1);

    divisions: number;
    scaleValues: number[] = [];
    showDivisions: boolean = true;
    showScaleValues: boolean = true;

    constructor(
        private dialogRef: MatDialogRef<GaugeDivisionsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: GaugeDivisionsDialogData,
        private gaugeExtension: GaugeExtensionService,
        private autosaveControlService: AutosaveControlService
    ) {}

    ngOnInit(): void {
        this.divisions = this.data.divisions;
        this.scaleValues = this.data.scaleValues ? [...this.data.scaleValues] : this.getAutoScaleValues();
        if (this.data.autoSavePage) {
            this.autosaveControlService.setDialogOpen(this.data.autoSavePage, true);
        }
    }

    ngOnDestroy(): void {
        if (this.data.autoSavePage) {
            this.autosaveControlService.setDialogOpen(this.data.autoSavePage, false);
        }
    }

    handleDivisionsChange(event: { name: string; value: number }) {
        this.divisions = Number(event.value);
        this.scaleValues = this.getAutoScaleValues();
    }

    handleScaleValueChange(index: number, event: { name: string; value: string }) {
        this.scaleValues[index] = event.value === '' ? NaN : Number(event.value);
    }

    formatScaleValue(value: number): string {
        return isNaN(value) ? '' : value.toFixed(this.data.decimalPlaces);
    }

    trackByIndex(index: number): number {
        return index;
    }

    cancel() {
        this.dialogRef.close();
    }

    save() {
        if (!this.isValid) return;
        const result: GaugeDivisionsDialogResult = { divisions: this.divisions, scaleValues: this.scaleValues };
        this.dialogRef.close(result);
    }

    private getAutoScaleValues(): number[] {
        return this.gaugeExtension
            .getAutoScaleValues(this.data.min, this.data.max, this.divisions + 1)
            .map(value => Number(value.toFixed(this.data.decimalPlaces)));
    }

    get isValid(): boolean {
        return this.scaleValues.every(
            (value, i) =>
                !isNaN(value) &&
                value >= this.data.min &&
                value <= this.data.max &&
                (i === 0 || value > this.scaleValues[i - 1])
        );
    }
}
